import { useMutation, useQuery } from '@tanstack/react-query';
import { httpService } from '../../../Helpers/Fetcher/axios';
import { exhibitorApi } from './constantExhibitor';
import {
  ICreateStall,
  TExhibitorInvoiceList,
  TFairAppliedStall,
  TSingleInvoice,
} from './exhibitorType';
import {
  ICompanyProfileInfoRegister,
  IExhibitorPubList,
  IFairLayoutPdf,
  IStalls,
} from '../../../Types/RegisterTypes';
import {
  commonAxiosError,
  commonAxiosSuccess,
  queryClient,
} from '../../../Utils/TanstackQuery/queryClient';
import {
  IAdditionalService,
  IAdditionalServiceList,
  IProduct,
} from '../../../Types/ProductTypes';
import { ExhibitorType } from '../../../Context/AuthContex/ExhibitorAuthContext';

/* PROFILE DATA GET */

export const useGetProfileData = () => {
  return useQuery({
    queryKey: [exhibitorApi.GET_EXHIBITOR_PROFILE],
    queryFn: () =>
      httpService
        .get<ExhibitorType>({
          url: exhibitorApi.GET_EXHIBITOR_PROFILE,
        })
        .catch(commonAxiosError()),
    enabled: true, // Disable automatic fetching
  });
};

/* space requirement */
export const useDeleteSpaceRequirement = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      httpService.delete({
        url: exhibitorApi.DELETE_APPLIED_STALL_LIST + '/' + id,
      }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Deleted!`);
      const queryKeysToInvalidate = [
        exhibitorApi.GET_APPLIED_STALL_LIST,
        exhibitorApi.GET_ALL_FAIR_STALL,
      ];
      queryKeysToInvalidate.forEach((key) =>
        queryClient.invalidateQueries({
          queryKey: [key],
        })
      );
    },
    onError: commonAxiosError(),
  });
};
export const useGetAllFairStallList = () => {
  return useQuery({
    queryKey: [exhibitorApi.GET_ALL_FAIR_STALL],
    queryFn: () =>
      httpService.get<IStalls[]>({
        url: exhibitorApi.GET_ALL_FAIR_STALL,
      }),
  });
};
export const useGetFairLayoutPdf = () => {
  return useQuery({
    queryKey: ['/api/common/fair-list'],
    queryFn: () =>
      httpService.get<IFairLayoutPdf>({
        url: '/api/common/fair-list',
      }),
  });
};
export const useGetExhibitorInvoiceList = () => {
  return useQuery({
    queryKey: [exhibitorApi.GET_EXHIBITOR_INVOICE_LIST],
    queryFn: () =>
      httpService.get<TExhibitorInvoiceList[]>({
        url: exhibitorApi.GET_EXHIBITOR_INVOICE_LIST,
      }),
  });
};
export const useViewExhibitorSingleInvoice = (id: string) => {
  return useQuery({
    queryKey: [exhibitorApi.GET_EXHIBITOR_INVOICE_LIST, id],
    queryFn: () =>
      httpService.get<TSingleInvoice>({
        url: `${exhibitorApi.GET_EXHIBITOR_INVOICE_LIST}/${id}`,
      }),
  });
};
export const useGetExhibitorStallList = () => {
  return useQuery({
    queryKey: [exhibitorApi.GET_APPLIED_STALL_LIST],
    queryFn: () =>
      httpService.get<TFairAppliedStall[]>({
        url: exhibitorApi.GET_APPLIED_STALL_LIST,
      }),
  });
};
export const useCreateSpaceRequirement = () => {
  return useMutation({
    mutationFn: ({ data }: { data: ICreateStall }) =>
      httpService.post<{ invoice_id: number }, {}>({
        url: exhibitorApi.CREATE_SPACE_FOR_STALL,
        data,
      }),

    onSuccess: () => {
      commonAxiosSuccess(`Successfully Booked Space!`);

      const queryKeysToInvalidate = [
        exhibitorApi.GET_APPLIED_STALL_LIST,
        exhibitorApi.GET_ALL_FAIR_STALL,
      ];
      queryKeysToInvalidate.forEach((key) =>
        queryClient.invalidateQueries({
          queryKey: [key],
        })
      );
      // queryClient.invalidateQueries({
      //   queryKey: [exhibitorApi.GET_APPLIED_STALL_LIST],
      // });
    },
    onError: commonAxiosError(),
  });
};
export const useGetExhibitorSearchList = (searchParams: {
  searchParams: string;
}) => {
  return useQuery({
    queryKey: [exhibitorApi.GET_EXHIBITOR_BY_SEARCH, searchParams],
    queryFn: () =>
      httpService
        .get<ICompanyProfileInfoRegister[]>({
          url: exhibitorApi.GET_EXHIBITOR_BY_SEARCH,
          params: searchParams,
        })
        .catch(commonAxiosError()),
    enabled: true, // Disable automatic fetching
  });
};
export const useGetExhibitorForPublicList = ({ name }: { name: string }) => {
  return useQuery({
    queryKey: [exhibitorApi.GET_ALL_EXHIBITOR_LIST, name],
    queryFn: () =>
      httpService
        .get<IExhibitorPubList[]>({
          url: exhibitorApi.GET_ALL_EXHIBITOR_LIST,
          params: { name },
        })
        .catch(commonAxiosError()),
  });
};

/* product and service list-------------------------- */

export const useGetProductAndServiceList = () => {
  return useQuery({
    queryKey: [exhibitorApi.GET_ALL_PRODUCT_AND_SERVICE_LIST],
    queryFn: () =>
      httpService
        .get<IProduct[]>({
          url: exhibitorApi.GET_ALL_PRODUCT_AND_SERVICE_LIST,
        })
        .catch(commonAxiosError()),
  });
};
export const useCreateProductAndService = () => {
  return useMutation({
    mutationFn: (data: FormData) =>
      httpService.post({
        url: exhibitorApi.ADD_PRODUCT_AND_SERVICE_LIST,
        data,
      }),

    onSuccess: () => {
      commonAxiosSuccess(`Successfully Added!`);
      queryClient.invalidateQueries({
        queryKey: [exhibitorApi.GET_ALL_PRODUCT_AND_SERVICE_LIST],
      });
    },
    onError: commonAxiosError(),
  });
};
export const useDeleteProductAndService = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      httpService.post({
        url: exhibitorApi.DELETE_PRODUCT_AND_SERVICE_LIST + '/' + id,
      }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Deleted!`);
      queryClient.invalidateQueries({
        queryKey: [exhibitorApi.GET_ALL_PRODUCT_AND_SERVICE_LIST],
      });
    },
    onError: commonAxiosError(),
  });
};
/* additional service list-------------------------- */

export const useGetAdditionalServiceList = () => {
  return useQuery({
    queryKey: [exhibitorApi.ADDITIONAL_SERVICE_LIST],
    queryFn: () =>
      httpService
        .get<IAdditionalServiceList[]>({
          url: exhibitorApi.ADDITIONAL_SERVICE_LIST,
        })
        .catch(commonAxiosError()),
  });
};
export const useGetSelectAdditionalServices = () => {
  return useQuery({
    queryKey: [exhibitorApi.GET_ADDITIONAL_SERVICES],
    queryFn: () =>
      httpService
        .get<IAdditionalService[]>({
          url: exhibitorApi.GET_ADDITIONAL_SERVICES,
        })
        .catch(commonAxiosError()),
  });
};
export const useCreateAdditionalService = () => {
  return useMutation({
    mutationFn: (data: {
      services: { quantity: number; service_id: number }[];
    }) =>
      httpService.post<{ invoice_id: number }, {}>({
        url: exhibitorApi.ADD_ADDITIONAL_SERVICE_LIST,
        data,
      }),

    onSuccess: () => {
      commonAxiosSuccess(`Successfully Added!`);
      queryClient.invalidateQueries({
        queryKey: [exhibitorApi.ADDITIONAL_SERVICE_LIST],
      });
    },
    onError: commonAxiosError(),
  });
};
export const useDeleteAdditionalService = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      httpService.delete({
        url: exhibitorApi.DELETE_ADDITIONAL_SERVICE_LIST + '/' + id,
      }),
    onSuccess: () => {
      commonAxiosSuccess(`Successfully Deleted!`);
      queryClient.invalidateQueries({
        queryKey: [exhibitorApi.DELETE_ADDITIONAL_SERVICE_LIST],
      });
    },
    onError: commonAxiosError(),
  });
};
